import { useContext, useState } from 'react';
import { Box, Button, Modal } from '@mui/material';
import { AuthContext, ViewContext } from 'components/lib';
import { useNavigate } from 'react-router-dom';
import {
  createOrganizationDetailsInitialState,
  OrganizationDetailsForm,
  type OrganizationDetailsFormErrors,
  type OrganizationDetailsFormState,
  validateOrganizationDetailsForm,
} from './OrganizationDetailsForm';
import { createAndActivateOrganization } from './createOrganizationFlow';
import { getApiErrorMessage } from 'api/apiClient';

type OrganizationRecord = {
  id: string | number;
};

type CreateOrganizationModalProps = {
  open: boolean;
  onClose: () => void;
  previousOrganizations?: OrganizationRecord[];
  onCreated?: (organizations: OrganizationRecord[]) => void | Promise<void>;
  redirectTo?: string;
};

export function CreateOrganizationModal({
  open,
  onClose,
  previousOrganizations = [],
  onCreated,
  redirectTo,
}: CreateOrganizationModalProps) {
  const authContext = useContext(AuthContext);
  const viewContext = useContext(ViewContext);
  const navigate = useNavigate();
  const [form, setForm] = useState<OrganizationDetailsFormState>(createOrganizationDetailsInitialState);
  const [errors, setErrors] = useState<OrganizationDetailsFormErrors>({});
  const [isCreating, setIsCreating] = useState(false);

  function handleFieldChange(name: keyof OrganizationDetailsFormState, value: string) {
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
    setErrors((current) => ({
      ...current,
      [name]: '',
    }));
  }

  function handleClose(force = false) {
    if (isCreating && !force) {
      return;
    }

    setForm(createOrganizationDetailsInitialState());
    setErrors({});
    onClose();
  }

  async function handleCreate() {
    if (isCreating) {
      return;
    }

    const nextErrors = validateOrganizationDetailsForm(form);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    try {
      setIsCreating(true);
      setErrors({});

      const result = await createAndActivateOrganization({
        form,
        refreshSession: authContext.refreshSession,
        previousOrganizations,
      });

      await onCreated?.(result.organizations);
      handleClose(true);
      viewContext.notification.show('Organization created', 'success', true, 'toast');
      if (redirectTo) {
        navigate(redirectTo);
      }
    } catch (error: unknown) {
      viewContext.notification.show(
        getApiErrorMessage(error, 'Unable to create your organization'),
        'error',
        true,
        'toast'
      );
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <Modal className="custom-modal" open={open} onClose={() => handleClose()}>
      <Box className="organization-create-modal">
        <div className="organization-action-modal__header">
          <div className="organization-action-modal__title">Create new Organization</div>
        </div>

        <div className="organization-create-modal__form">
          <OrganizationDetailsForm
            form={form}
            errors={errors}
            onFieldChange={handleFieldChange}
            showOwnerHint={false}
            idPrefix="create-organization"
          />
        </div>

        <div className="organization-action-modal__actions">
          <Button
            variant="contained"
            className="modal-no-btn organization-action-modal__cancel"
            onClick={() => handleClose()}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            className="modal-yes-btn organization-action-modal__confirm"
            onClick={handleCreate}
            disabled={isCreating}
          >
            {isCreating ? 'Creating...' : 'Create'}
          </Button>
        </div>
      </Box>
    </Modal>
  );
}
